import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Play, Sparkles } from "lucide-react";
import ParticleField from "./ParticleField";
import { Button } from "./ui/button";
import { COMPANY } from "../data/content";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.2 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const HEADLINE = ["Transforming Businesses", "Through Intelligent", "Software."];

const Hero = () => {
  return (
    <section id="top" className="relative min-h-screen overflow-hidden pt-32 sm:pt-40" data-testid="hero-section">
      {/* Background layers */}
      <div className="pointer-events-none absolute inset-0 grid-bg radial-fade opacity-60" />
      <ParticleField className="absolute inset-0 h-full w-full" />
      <div className="pointer-events-none absolute -left-40 top-20 h-96 w-96 rounded-full bg-teal/20 blur-[120px]" />
      <div className="pointer-events-none absolute -right-40 top-40 h-96 w-96 rounded-full bg-navy/20 blur-[120px]" />

      <div className="relative mx-auto flex max-w-[1400px] flex-col items-center px-6 text-center sm:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mb-8 inline-flex items-center gap-2 rounded-full border border-border glass px-4 py-1.5 text-sm font-medium text-foreground"
          data-testid="hero-badge"
        >
          <Sparkles className="h-4 w-4 text-teal" />
          Innovative AI Software · UK &amp; Nigeria
        </motion.div>

        <h1 className="font-display text-4xl font-bold leading-[1.05] tracking-tighter text-foreground sm:text-6xl lg:text-7xl">
          {HEADLINE.map((l, i) => (
            <span key={i} className="block overflow-hidden py-0.5">
              <motion.span
                custom={i}
                variants={line}
                initial="hidden"
                animate="show"
                className="block"
              >
                {i === 2 ? (
                  <span>
                    Software<span className="text-teal">.</span>
                  </span>
                ) : (
                  l
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.85 }}
          className="mt-7 max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg"
        >
          We build enterprise software that helps organisations become smarter, safer and more
          productive through AI, cloud technology and digital innovation.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.0 }}
          className="mt-10 flex flex-col items-center gap-3 sm:flex-row"
        >
          <a href="#contact">
            <Button
              size="lg"
              data-testid="hero-book-demo"
              className="group h-12 rounded-full bg-navy px-7 text-base font-semibold text-white shadow-lg shadow-navy/20 hover:bg-navy/90"
            >
              Book a Demo
              <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>
          </a>
          <a href="#solutions">
            <Button
              size="lg"
              variant="outline"
              data-testid="hero-explore"
              className="h-12 rounded-full border-2 px-7 text-base font-semibold hover:border-teal hover:text-teal"
            >
              Explore Our Solutions
            </Button>
          </a>
          <a href="#feature">
            <Button
              size="lg"
              variant="ghost"
              data-testid="hero-watch-video"
              className="group h-12 rounded-full px-4 text-base font-semibold"
            >
              <span className="mr-2 flex h-9 w-9 items-center justify-center rounded-full bg-teal/15 text-teal transition-transform group-hover:scale-110">
                <Play className="h-4 w-4 fill-current" />
              </span>
              Watch Video
            </Button>
          </a>
        </motion.div>

        {/* Floating dashboard preview */}
        <motion.div
          initial={{ opacity: 0, y: 60, rotateX: 18 }}
          animate={{ opacity: 1, y: 0, rotateX: 0 }}
          transition={{ duration: 1.1, delay: 1.15, ease: [0.22, 1, 0.36, 1] }}
          className="relative mt-20 w-full max-w-5xl"
          style={{ perspective: 1200 }}
          data-testid="hero-preview"
        >
          <div className="animate-float overflow-hidden rounded-2xl border border-border bg-card shadow-2xl shadow-navy/10">
            <img
              src="https://customer-assets-cm19k8pv.emergentagent.net/job_enterprise-ai-hub-80/artifacts/9y8rvvbi_image.png"
              alt="AYOBELL Care Manager Dashboard preview"
              className="w-full"
              loading="eager"
            />
          </div>
          <div className="pointer-events-none absolute inset-x-0 -bottom-1 h-32 bg-gradient-to-t from-background to-transparent" />
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
